import React, { useState, useMemo, useRef } from 'react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { 
  Building2, 
  DollarSign, 
  Calculator as CalcIcon,
  Download,
  Landmark,
  Wallet,
  Settings,
  FileStack,
  Construction,
  ArrowRightLeft,
  RefreshCw,
  Banknote,
  Receipt,
  PiggyBank,
  CreditCard,
  Save,
  FolderOpen,
  FileText,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { calculateAllMetrics, CLOSING_COST_RATE } from '../utils/calculations';
import { 
  calculateBridgeRefinanceMetrics, 
  calculateBridgePurchaseMetrics,
  BRIDGE_TRANSACTION_TYPES,
  FEE_RATES 
} from '../utils/bridgeCalculations';
import { calculatePortfolioMetrics } from '../utils/portfolioCalculations';
import { formatCurrency, formatPercentage } from '../utils/formatters';
import { LoanSummaryPDF, BridgeLoanSummaryPDF, PortfolioLoanSummaryPDF } from './PDFDocument';
import SavedScenarios from './SavedScenarios';
import CompareView from './CompareView';
import ChurchillDSCRCalculator from './ChurchillDSCRCalculator';
import LoanSizer from './LoanSizer';
import IOIGenerator from './IOIGenerator';
import { calculateChurchillMetrics } from '../utils/churchillCalculations';
import { hasTemplateAccess, TEMPLATE_TYPES } from '../config/loanOfficers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Loan types
const LOAN_TYPES = {
  CONSTRUCTION: 'construction',
  BRIDGE: 'bridge',
  PORTFOLIO: 'portfolio',
  CHURCHILL_DSCR: 'churchill_dscr',
};

// Interest Reserve Month options
const INTEREST_RESERVE_OPTIONS = [0, 6, 12, 18, 24];

// Pre-paid interest days options
const PREPAID_INTEREST_DAYS_OPTIONS = [15, 30, 45];

// Amortization term options (years)
const AMORTIZATION_TERM_OPTIONS = [15, 20, 25, 30];

// Initial empty state for Construction
const initialInputs = {
  // Loan Type
  loanType: '',
  
  // Property Info
  projectName: '',
  propertyAddress: '',
  
  // Loan Structure
  loanTerm: '',
  interestRate: '',
  rateType: 'Variable',
  interestReserveMonths: '0',
  targetLTV: '',
  targetLTC: '',
  asIsValue: '',
  asCompletedValue: '',
  
  // Project Costs
  acquisitionCost: '',
  hardCosts: '',
  softCosts: '',
  financingCosts: '',
  
  // Existing Capital
  equityOnPurchase: '',
  existingDebt: '',
  spentToDate: '',
  
  // Optional
  propertyTaxReserve: '',
  inspectionFees: '',
  additionalHoldback: '',
};

// Initial empty state for Bridge Loan
const initialBridgeInputs = {
  // Loan Type
  loanType: 'bridge',
  transactionType: '',
  
  // Property Info
  projectName: '',
  propertyAddress: '',
  
  // Property Value & Loan
  propertyValue: '',
  loanAmount: '',
  
  // Loan Structure
  loanTerm: '12',
  interestRate: '',
  rateType: 'Fixed',
  interestReserveMonths: '0',
  prePaidInterestDays: '15',
  
  // Origination Fees (default 2% of loan amount, entered as percentage)
  lenderOriginationFeePercent: '2',
  brokerOriginationFeePercent: '2',
  
  // Refinance specific
  payoffAmount: '',
  
  // Purchase specific
  purchasePrice: '',
  downPayment: '',
  afterRepairValue: '',
  
  // Loan Fees (with new defaults)
  lenderDueDiligence: '5000',
  processingFee: '5000',
  titleSearch: '275',
  lienSearch: '200',
  titleEndorsements: '375',
  wireFee: '35',
};

// Portfolio Transaction Types
const PORTFOLIO_TRANSACTION_TYPES = {
  DEVELOPER: 'developer',
  REFINANCE_CASHOUT: 'refinance_cashout',
  PURCHASE: 'purchase',
};

// Initial empty state for Portfolio/Developer Loan
const initialPortfolioInputs = {
  // Loan Type
  loanType: 'portfolio',
  transactionType: '',
  
  // Property Info
  projectName: '',
  propertyAddress: '',
  
  // Property Value & Loan - LTV is INPUT, loan calculated
  purchasePrice: '',
  targetLTV: '75',
  existingMortgageBalance: '', // For Refinance Cash-Out only
  
  // Loan Structure
  interestRate: '',
  rateType: 'Fixed',
  amortizationTerm: '30',
  
  // Monthly Costs (for qualification)
  taxes: '',
  insurance: '',
  hoa: '',
  
  // Lender Fees
  originationPoints: '0.75',
  appraisalFee: '3500',
  processingFee: '750',
  
  // Developer Costs
  developerFeePercent: '',
  capitalContributionMonths: '',
  
  // Deposit & Credits
  depositPercent: '40',
  developerCredit: '',
  lenderCredit: '',
  
  // Pre-Paids
  escrowTaxMonths: '3',
  escrowInsuranceMonths: '6',
  prePaidInterestDays: '15',
};

// Initial state for Churchill DSCR
const initialChurchillInputs = {
  loanType: LOAN_TYPES.CHURCHILL_DSCR,
  
  // Property Info
  projectName: '',
  propertyAddress: '',
  
  // Loan Details
  purchasePrice: '',
  loanAmount: '',
  
  // Borrower Info
  fico: '',
  isForeignNational: false,
  
  // Property & Terms
  propertyType: 'single_family',
  loanPurpose: 'purchase',
  prepayPenalty: '3_year',
  
  // DSCR Information
  dscrRatio: '',
  monthlyRent: '',
  monthlyPITIA: '',
  
  // Loan Structure
  interestRate: '',
  amortizationTerm: '30',
  
  // Target Pricing
  rateSheetProgram: '',
  maxYieldMode: false,
  minYSPTarget: '',
  rateLockDays: '30',
  brokerCompPercent: '',
  extensionDays: '0',
};

// Churchill DSCR Options
const CHURCHILL_PROPERTY_TYPES = [
  { value: 'single_family', label: 'Single Family' },
  { value: 'condo', label: 'Condo' },
  { value: '2_4_units', label: '2-4 Units' },
  { value: '5_plus_units', label: '5+ Units' },
];

const CHURCHILL_LOAN_PURPOSES = [
  { value: 'purchase', label: 'Purchase' },
  { value: 'rate_term_refi', label: 'Rate/Term Refinance' },
  { value: 'cash_out_refi', label: 'Cash-Out Refinance' },
];

const CHURCHILL_PREPAY_OPTIONS = [
  { value: 'none', label: 'None (-1.50)', adjustment: -1.50 },
  { value: '1_year', label: '1 Year (-0.75)', adjustment: -0.75 },
  { value: '2_year', label: '2 Year (-0.25)', adjustment: -0.25 },
  { value: '3_year', label: '3 Year (Par)', adjustment: 0 },
  { value: '4_year', label: '4 Year (+0.50)', adjustment: 0.50 },
  { value: '5_year', label: '5 Year (+1.25)', adjustment: 1.25 },
];

const CHURCHILL_RATE_LOCK_DAYS = [15, 30, 45, 60, 90];
const CHURCHILL_EXTENSION_DAYS = [0, 7, 14, 21, 30];

const CurrencyInput = ({ value, onChange, placeholder, id, testId }) => {
  const [displayValue, setDisplayValue] = useState(value ? formatCurrency(value).replace('$', '') : '');

  const handleChange = (e) => {
    const rawValue = e.target.value.replace(/[^0-9.]/g, '');
    setDisplayValue(rawValue);
    onChange(rawValue);
  };

  const handleBlur = () => {
    if (value) {
      setDisplayValue(formatCurrency(parseFloat(value)).replace('$', ''));
    }
  };

  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#637888] font-mono">$</span>
      <Input
        id={id}
        data-testid={testId}
        type="text"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        className="pl-7 font-mono-number"
      />
    </div>
  );
};

const PercentageInput = ({ value, onChange, placeholder, id, testId }) => {
  return (
    <div className="relative">
      <Input
        id={id}
        data-testid={testId}
        type="number"
        step="0.1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="pr-7 font-mono-number"
      />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#637888] font-mono">%</span>
    </div>
  );
};

export const Calculator = ({ loanOfficer, onLogout }) => {
  const [inputs, setInputs] = useState(initialInputs);
  const [bridgeInputs, setBridgeInputs] = useState(initialBridgeInputs);
  const [portfolioInputs, setPortfolioInputs] = useState(initialPortfolioInputs);
  const [churchillInputs, setChurchillInputs] = useState(initialChurchillInputs);
  
  // Save & Compare state
  const sizerControlsRef = useRef({});
  const ioiControlsRef = useRef({});
  const [savedScenariosOpen, setSavedScenariosOpen] = useState(false);
  const [compareViewOpen, setCompareViewOpen] = useState(false);
  const [compareScenarios, setCompareScenarios] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Rate sheet programs (loaded from backend)
  const [rateSheetPrograms, setRateSheetPrograms] = useState([]);

  const updateInput = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const updateBridgeInput = (field, value) => {
    setBridgeInputs(prev => ({ ...prev, [field]: value }));
  };

  const updatePortfolioInput = (field, value) => {
    setPortfolioInputs(prev => ({ ...prev, [field]: value }));
  };

  const updateChurchillInput = (field, value) => {
    setChurchillInputs(prev => ({ ...prev, [field]: value }));
  };

  // Load available rate sheet programs
  React.useEffect(() => {
    const loadRateSheetPrograms = async () => {
      try {
        const response = await fetch(`${API_URL}/api/rate-sheets-programs`);
        if (response.ok) {
          const data = await response.json();
          setRateSheetPrograms(data.programs || []);
        }
      } catch (error) {
        console.error('Failed to load rate sheet programs:', error);
      }
    };
    loadRateSheetPrograms();
  }, []);

  // Handle loan type change
  const handleLoanTypeChange = (value) => {
    if (value === LOAN_TYPES.CONSTRUCTION) {
      setInputs(prev => ({ ...prev, loanType: value }));
    } else if (value === LOAN_TYPES.BRIDGE) {
      setBridgeInputs(prev => ({ ...prev, loanType: value }));
    } else if (value === LOAN_TYPES.PORTFOLIO) {
      setPortfolioInputs(prev => ({ ...prev, loanType: value }));
    } else if (value === LOAN_TYPES.CHURCHILL_DSCR) {
      setChurchillInputs(prev => ({ ...prev, loanType: value }));
    }
    // Store current loan type in all states for tracking
    setInputs(prev => ({ ...prev, loanType: value }));
    setBridgeInputs(prev => ({ ...prev, loanType: value }));
    setPortfolioInputs(prev => ({ ...prev, loanType: value }));
    setChurchillInputs(prev => ({ ...prev, loanType: value }));
  };

  // Calculate all metrics whenever inputs change
  const metrics = useMemo(() => {
    return calculateAllMetrics(inputs);
  }, [inputs]);

  // Calculate Bridge metrics
  const bridgeMetrics = useMemo(() => {
    if (bridgeInputs.transactionType === BRIDGE_TRANSACTION_TYPES.REFINANCE_CASHOUT) {
      return calculateBridgeRefinanceMetrics(bridgeInputs);
    } else if (bridgeInputs.transactionType === BRIDGE_TRANSACTION_TYPES.PURCHASE) {
      return calculateBridgePurchaseMetrics(bridgeInputs);
    }
    return null;
  }, [bridgeInputs]);

  // Calculate Portfolio metrics
  const portfolioMetrics = useMemo(() => {
    return calculatePortfolioMetrics(portfolioInputs);
  }, [portfolioInputs]);

  // Calculate Churchill DSCR metrics
  const churchillMetrics = useMemo(() => {
    return calculateChurchillMetrics(churchillInputs);
  }, [churchillInputs]);

  // Check if we have enough data to show results
  const hasResults = metrics.totalProjectCost > 0 || metrics.finalLoanAmount > 0;
  const hasBridgeResults = bridgeMetrics && bridgeMetrics.loanAmount > 0;
  const hasPortfolioResults = portfolioMetrics && portfolioMetrics.loanAmount > 0;
  const hasChurchillResults = churchillMetrics && churchillMetrics.loanAmount > 0;

  // Check loan types
  const isConstructionLoan = inputs.loanType === LOAN_TYPES.CONSTRUCTION;
  const isBridgeLoan = inputs.loanType === LOAN_TYPES.BRIDGE;
  const isPortfolioLoan = inputs.loanType === LOAN_TYPES.PORTFOLIO;
  const isChurchillLoan = inputs.loanType === LOAN_TYPES.CHURCHILL_DSCR;
  const isIOI = inputs.loanType === LOAN_TYPES.IOI;
  const hasLoanType = inputs.loanType !== '';
  
  // Check Bridge transaction type
  const isRefinanceCashOut = bridgeInputs.transactionType === BRIDGE_TRANSACTION_TYPES.REFINANCE_CASHOUT;
  const isPurchase = bridgeInputs.transactionType === BRIDGE_TRANSACTION_TYPES.PURCHASE;
  const hasBridgeTransactionType = bridgeInputs.transactionType !== '';

  // Get current loan type for save
  const getCurrentLoanTypeForSave = () => {
    const currentLoanType = inputs.loanType;
    if (currentLoanType === LOAN_TYPES.BRIDGE) {
      return bridgeInputs.transactionType === BRIDGE_TRANSACTION_TYPES.PURCHASE 
        ? 'bridge_purchase' 
        : 'bridge_refinance';
    }
    return currentLoanType;
  };

  // Get current inputs and metrics for save
  const getCurrentDataForSave = () => {
    const currentLoanType = inputs.loanType;
    if (currentLoanType === LOAN_TYPES.CONSTRUCTION) {
      return null; // Construction sizer has its own Save/Load controls
    } else if (currentLoanType === LOAN_TYPES.BRIDGE) {
      return { inputs: bridgeInputs, metrics: bridgeMetrics, address: bridgeInputs.propertyAddress };
    } else if (currentLoanType === LOAN_TYPES.PORTFOLIO) {
      return { inputs: portfolioInputs, metrics: portfolioMetrics, address: portfolioInputs.propertyAddress };
    } else if (currentLoanType === LOAN_TYPES.CHURCHILL_DSCR) {
      return { inputs: churchillInputs, metrics: churchillMetrics, address: churchillInputs.propertyAddress };
    }
    return null;
  };

  // Save current scenario
  const handleSaveScenario = async () => {
    if (!loanOfficer?.id) return;
    
    const data = getCurrentDataForSave();
    if (!data) return;
    
    if (!data.address) {
      alert('Please enter a property address before saving.');
      return;
    }
    
    setSaving(true);
    setSaveSuccess(false);
    
    try {
      const response = await fetch(`${API_URL}/api/scenarios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          officer_id: loanOfficer.id,
          loan_type: getCurrentLoanTypeForSave(),
          property_address: data.address,
          project_name: data.inputs.projectName || '',
          inputs: data.inputs,
          metrics: data.metrics
        })
      });
      
      if (!response.ok) throw new Error('Failed to save scenario');
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save scenario:', error);
      alert('Failed to save scenario. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Load a saved scenario
  const handleLoadScenario = (scenario) => {
    const { loan_type, inputs: savedInputs } = scenario;
    
    // Determine base loan type
    let baseLoanType;
    if (loan_type === 'construction') {
      baseLoanType = LOAN_TYPES.CONSTRUCTION;
    } else if (loan_type === 'bridge_refinance' || loan_type === 'bridge_purchase') {
      baseLoanType = LOAN_TYPES.BRIDGE;
    } else if (loan_type === 'portfolio') {
      baseLoanType = LOAN_TYPES.PORTFOLIO;
    } else if (loan_type === 'churchill_dscr') {
      baseLoanType = LOAN_TYPES.CHURCHILL_DSCR;
    }
    
    // Update loan type across all states
    handleLoanTypeChange(baseLoanType);
    
    // Load the appropriate inputs
    if (loan_type === 'construction') {
      setInputs({ ...initialInputs, ...savedInputs, loanType: baseLoanType });
    } else if (loan_type === 'bridge_refinance' || loan_type === 'bridge_purchase') {
      setBridgeInputs({ ...initialBridgeInputs, ...savedInputs, loanType: baseLoanType });
    } else if (loan_type === 'portfolio') {
      setPortfolioInputs({ ...initialPortfolioInputs, ...savedInputs, loanType: baseLoanType });
    } else if (loan_type === 'churchill_dscr') {
      setChurchillInputs({ ...initialChurchillInputs, ...savedInputs, loanType: baseLoanType });
    }
  };

  // Handle compare scenarios
  const handleCompareScenarios = (scenarios) => {
    setCompareScenarios(scenarios);
    setCompareViewOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-[#002F87] text-white px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/vaster_logo.svg" 
              alt="Vaster" 
              className="h-8 invert"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <span className="font-heading font-extrabold text-2xl tracking-tighter hidden">VASTER</span>
          </div>
          
          <h1 className="font-heading font-semibold text-lg tracking-tight">
            Simulation Station
          </h1>
          
          {/* Save & Compare + Loan Officer Info */}
          {loanOfficer && (
            <div className="flex items-center gap-3">
              {/* Save & Compare Buttons */}
              <div className="flex items-center gap-2 mr-4">
                <button
                  onClick={() => {
                    if (inputs.loanType === LOAN_TYPES.CONSTRUCTION) {
                      sizerControlsRef.current.openSave?.();
                    } else if (inputs.loanType === LOAN_TYPES.IOI) {
                      ioiControlsRef.current.openSave?.();
                    } else {
                      handleSaveScenario();
                    }
                  }}
                  disabled={saving || (inputs.loanType !== LOAN_TYPES.CONSTRUCTION && inputs.loanType !== LOAN_TYPES.IOI && !getCurrentDataForSave()?.address)}
                  data-testid="btn-save-scenario"
                  className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded transition-colors ${
                    saveSuccess 
                      ? 'bg-emerald-500 text-white' 
                      : 'bg-[#FFFF00] text-[#002F87] hover:bg-[#E6E600]'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {saving ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : saveSuccess ? (
                    <>✓ Saved</>
                  ) : (
                    <>
                      <Save className="w-3.5 h-3.5" />
                      Save
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    if (inputs.loanType === LOAN_TYPES.CONSTRUCTION) {
                      sizerControlsRef.current.openLoad?.();
                    } else if (inputs.loanType === LOAN_TYPES.IOI) {
                      ioiControlsRef.current.openLoad?.();
                    } else {
                      setSavedScenariosOpen(true);
                    }
                  }}
                  data-testid="btn-open-scenarios"
                  className="flex items-center gap-1.5 text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded transition-colors"
                >
                  <FolderOpen className="w-3.5 h-3.5" />
                  Scenarios
                </button>
              </div>
              
              {/* Officer Info */}
              <div className="text-right">
                <p className="text-sm font-medium">{loanOfficer.name}</p>
                <p className="text-xs text-white/70">{loanOfficer.title}</p>
              </div>
              <button
                onClick={onLogout}
                data-testid="btn-logout"
                className="text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded transition-colors"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-6 lg:p-8">
        {/* Loan Type Selection */}
        <Card className="border border-slate-200 shadow-sm mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
              <FileStack className="w-4 h-4" strokeWidth={1.5} />
              Template Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-w-md">
              <Label htmlFor="loanType" className="text-xs text-[#637888] uppercase tracking-wide">
                Select Template Type <span className="text-red-500">*</span>
              </Label>
              <Select
                value={inputs.loanType}
                onValueChange={handleLoanTypeChange}
              >
                <SelectTrigger data-testid="select-loan-type" className="mt-1">
                  <SelectValue placeholder="Select template type..." />
                </SelectTrigger>
                <SelectContent>
                  {hasTemplateAccess(loanOfficer, TEMPLATE_TYPES.CONSTRUCTION) && (
                    <SelectItem value={LOAN_TYPES.CONSTRUCTION}>
                      <div className="flex items-center gap-2">
                        <Construction className="w-4 h-4" />
                        Construction Loan
                      </div>
                    </SelectItem>
                  )}
                  {hasTemplateAccess(loanOfficer, TEMPLATE_TYPES.BRIDGE) && (
                    <SelectItem value={LOAN_TYPES.BRIDGE}>
                      <div className="flex items-center gap-2">
                        <ArrowRightLeft className="w-4 h-4" />
                        Bridge Loan
                      </div>
                    </SelectItem>
                  )}
                  {hasTemplateAccess(loanOfficer, TEMPLATE_TYPES.PORTFOLIO) && (
                    <SelectItem value={LOAN_TYPES.PORTFOLIO}>
                      <div className="flex items-center gap-2">
                        <PiggyBank className="w-4 h-4" />
                        Portfolio
                      </div>
                    </SelectItem>
                  )}
                  {hasTemplateAccess(loanOfficer, TEMPLATE_TYPES.CHURCHILL_DSCR) && (
                    <SelectItem value={LOAN_TYPES.CHURCHILL_DSCR}>
                      <div className="flex items-center gap-2">
                        <Landmark className="w-4 h-4" />
                        Churchill DSCR
                      </div>
                    </SelectItem>
                  )}
                  {hasTemplateAccess(loanOfficer, TEMPLATE_TYPES.IOI) && (
                    <SelectItem value={LOAN_TYPES.IOI}>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Indication of Interest
                      </div>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Bridge Loan Calculator */}
        {isBridgeLoan && (
          <div className="space-y-6">
            {/* Transaction Type Selection */}
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                  <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
                  Transaction Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-w-md">
                  <Label htmlFor="transactionType" className="text-xs text-[#637888] uppercase tracking-wide">
                    Select Transaction Type <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={bridgeInputs.transactionType}
                    onValueChange={(v) => updateBridgeInput('transactionType', v)}
                  >
                    <SelectTrigger data-testid="select-transaction-type" className="mt-1">
                      <SelectValue placeholder="Select transaction type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={BRIDGE_TRANSACTION_TYPES.REFINANCE_CASHOUT}>
                        <div className="flex items-center gap-2">
                          <Banknote className="w-4 h-4" />
                          Refinance Cash-Out
                        </div>
                      </SelectItem>
                      <SelectItem value={BRIDGE_TRANSACTION_TYPES.PURCHASE}>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          Purchase
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Purchase Calculator */}
            {isPurchase && (
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                {/* Input Sections - Left Column */}
                <div className="lg:col-span-3 space-y-6">
                  {/* Property Information */}
                  <Card className="border border-slate-200 shadow-sm">
                    <CardHeader className="pb-4">
                      <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                        <Building2 className="w-4 h-4" strokeWidth={1.5} />
                        Property Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <Label htmlFor="purchaseProjectName" className="text-xs text-[#637888] uppercase tracking-wide">
                            Project Name
                          </Label>
                          <Input
                            id="purchaseProjectName"
                            data-testid="input-purchase-project-name"
                            value={bridgeInputs.projectName}
                            onChange={(e) => updateBridgeInput('projectName', e.target.value)}
                            placeholder="Enter project name"
                            className="mt-1"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label htmlFor="purchasePropertyAddress" className="text-xs text-[#637888] uppercase tracking-wide">
                            Property Address
                          </Label>
                          <Input
                            id="purchasePropertyAddress"
                            data-testid="input-purchase-property-address"
                            value={bridgeInputs.propertyAddress}
                            onChange={(e) => updateBridgeInput('propertyAddress', e.target.value)}
                            placeholder="Enter property address"
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Purchase Details */}
                  <Card className="border border-slate-200 shadow-sm">
                    <CardHeader className="pb-4">
                      <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                        <DollarSign className="w-4 h-4" strokeWidth={1.5} />
                        Purchase Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="purchasePrice" className="text-xs text-[#637888] uppercase tracking-wide">
                            Purchase Price
                          </Label>
                          <CurrencyInput
                            id="purchasePrice"
                            testId="input-purchase-price"
                            value={bridgeInputs.purchasePrice}
                            onChange={(v) => updateBridgeInput('purchasePrice', v)}
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <Label htmlFor="purchaseLoanAmount" className="text-xs text-[#637888] uppercase tracking-wide">
                            Loan Amount
                          </Label>
                          <CurrencyInput
                            id="purchaseLoanAmount"
                            testId="input-purchase-loan-amount"
                            value={bridgeInputs.loanAmount}
                            onChange={(v) => updateBridgeInput('loanAmount', v)}
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <Label htmlFor="purchaseDownPayment" className="text-xs text-[#637888] uppercase tracking-wide">
                            Down Payment
                          </Label>
                          <CurrencyInput
                            id="purchaseDownPayment"
                            testId="input-purchase-down-payment"
                            value={bridgeInputs.downPayment}
                            onChange={(v) => updateBridgeInput('downPayment', v)}
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <Label htmlFor="afterRepairValue" className="text-xs text-[#637888] uppercase tracking-wide">
                            After Repair Value (ARV)
                          </Label>
                          <CurrencyInput
                            id="afterRepairValue"
                            testId="input-purchase-arv"
                            value={bridgeInputs.afterRepairValue}
                            onChange={(v) => updateBridgeInput('afterRepairValue', v)}
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-[#637888] uppercase tracking-wide">
                            Purchase LTV
                          </Label>
                          <div className="mt-1 p-2 bg-[#EAF5FD] border border-slate-200 rounded-md">
                            <span className="font-mono-number text-[#002F87] font-semibold" data-testid="display-purchase-ltv">
                              {formatPercentage(bridgeMetrics?.purchaseLTV || 0)}
                            </span>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-[#637888] uppercase tracking-wide">
                            ARV LTV
                          </Label>
                          <div className="mt-1 p-2 bg-[#EAF5FD] border border-slate-200 rounded-md">
                            <span className="font-mono-number text-[#002F87] font-semibold" data-testid="display-arv-ltv">
                              {formatPercentage(bridgeMetrics?.arvLTV || 0)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Loan Structure */}
                  <Card className="border border-slate-200 shadow-sm">
                    <CardHeader className="pb-4">
                      <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                        <Landmark className="w-4 h-4" strokeWidth={1.5} />
                        Loan Structure
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="purchaseLoanTerm" className="text-xs text-[#637888] uppercase tracking-wide">
                            Loan Term (months)
                          </Label>
                          <Input
                            id="purchaseLoanTerm"
                            data-testid="input-purchase-loan-term"
                            type="number"
                            value={bridgeInputs.loanTerm}
                            onChange={(e) => updateBridgeInput('loanTerm', e.target.value)}
                            placeholder="e.g., 24"
                            className="mt-1 font-mono-number"
                          />
                        </div>
                        <div>
                          <Label htmlFor="purchaseInterestRate" className="text-xs text-[#637888] uppercase tracking-wide">
                            Interest Rate
                          </Label>
                          <div className="flex gap-2 mt-1">
                            <div className="flex-1">
                              <PercentageInput
                                id="purchaseInterestRate"
                                testId="input-purchase-interest-rate"
                                value={bridgeInputs.interestRate}
                                onChange={(v) => updateBridgeInput('interestRate', v)}
                                placeholder="e.g., 10"
                              />
                            </div>
                            <Select
                              value={bridgeInputs.rateType}
                              onValueChange={(v) => updateBridgeInput('rateType', v)}
                            >
                              <SelectTrigger data-testid="select-purchase-rate-type" className="w-28">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Variable">Variable</SelectItem>
                                <SelectItem value="Fixed">Fixed</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="purchaseInterestReserve" className="text-xs text-[#637888] uppercase tracking-wide">
                            Interest Reserve (Months)
                          </Label>
                          <Select
                            value={bridgeInputs.interestReserveMonths}
                            onValueChange={(v) => updateBridgeInput('interestReserveMonths', v)}
                          >
                            <SelectTrigger data-testid="select-purchase-interest-reserve" className="mt-1">
                              <SelectValue placeholder="Select months" />
                            </SelectTrigger>
                            <SelectContent>
                              {INTEREST_RESERVE_OPTIONS.map((months) => (
                                <SelectItem key={months} value={String(months)}>
                                  {months} {months === 1 ? 'month' : 'months'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-[#637888] uppercase tracking-wide">
                            Monthly Payment (IO)
                          </Label>
                          <div className="mt-1 p-2 bg-[#EAF5FD] border border-slate-200 rounded-md">
                            <span className="font-mono-number text-[#002F87] font-semibold" data-testid="display-purchase-monthly">
                              {formatCurrency(bridgeMetrics?.monthlyPayment || 0)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Origination Fees */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-200">
                        <div>
                          <Label htmlFor="purchaseLenderOrigination" className="text-xs text-[#637888] uppercase tracking-wide">
                            Lender Origination Fee (%)
                          </Label>
                          <PercentageInput
                            id="purchaseLenderOrigination"
                            testId="input-purchase-lender-origination-percent"
                            value={bridgeInputs.lenderOriginationFeePercent}
                            onChange={(v) => updateBridgeInput('lenderOriginationFeePercent', v)}
                            placeholder="2"
                          />
                          <div className="mt-2 p-2 bg-[#EAF5FD] border border-slate-200 rounded-md">
                            <span className="text-xs text-[#637888] uppercase tracking-wide">Calculated: </span>
                            <span className="font-mono-number text-[#002F87] font-semibold" data-testid="display-purchase-lender-origination">
                              {formatCurrency(bridgeMetrics?.lenderOriginationFee || 0)}
                            </span>
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="purchaseBrokerOrigination" className="text-xs text-[#637888] uppercase tracking-wide">
                            Broker Origination Fee (%)
                          </Label>
                          <PercentageInput
                            id="purchaseBrokerOrigination"
                            testId="input-purchase-broker-origination-percent"
                            value={bridgeInputs.brokerOriginationFeePercent}
                            onChange={(v) => updateBridgeInput('brokerOriginationFeePercent', v)}
                            placeholder="2"
                          />
                          <div className="mt-2 p-2 bg-[#EAF5FD] border border-slate-200 rounded-md">
                            <span className="text-xs text-[#637888] uppercase tracking-wide">Calculated: </span>
                            <span className="font-mono-number text-[#002F87] font-semibold" data-testid="display-purchase-broker-origination">
                              {formatCurrency(bridgeMetrics?.brokerOriginationFee || 0)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Loan Fees - Purchase */}
                  <Card className="border border-slate-200 shadow-sm">
                    <CardHeader className="pb-4">
                      <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                        <Settings className="w-4 h-4" strokeWidth={1.5} />
                        Loan Fees
                      </CardTitle>
                      <p className="text-xs text-slate-500 mt-1">Percentage-based fees auto-calculated from loan amount</p>
                    </CardHeader>
                    <CardContent>
                      {/* Auto-calculated percentage fees */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 p-3 bg-slate-50 rounded-lg">
                        <div>
                          <Label className="text-xs text-[#637888] uppercase tracking-wide">
                            Lender's Title Policy (0.081%)
                          </Label>
                          <div className="mt-1 p-2 bg-white border border-slate-200 rounded-md">
                            <span className="font-mono-number text-slate-700">
                              {formatCurrency(bridgeMetrics?.lendersTitlePolicy || 0)}
                            </span>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-[#637888] uppercase tracking-wide">
                            Documentary Stamps (0.35%)
                          </Label>
                          <div className="mt-1 p-2 bg-white border border-slate-200 rounded-md">
                            <span className="font-mono-number text-slate-700">
                              {formatCurrency(bridgeMetrics?.documentaryStamps || 0)}
                            </span>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-[#637888] uppercase tracking-wide">
                            Owner's Title Ins. (0.42%)
                          </Label>
                          <div className="mt-1 p-2 bg-white border border-slate-200 rounded-md">
                            <span className="font-mono-number text-slate-700">
                              {formatCurrency(bridgeMetrics?.ownersTitleInsurance || 0)}
                            </span>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-[#637888] uppercase tracking-wide">
                            Intangible Stamps (0.20%)
                          </Label>
                          <div className="mt-1 p-2 bg-white border border-slate-200 rounded-md">
                            <span className="font-mono-number text-slate-700">
                              {formatCurrency(bridgeMetrics?.intangibleTax || 0)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Editable fixed fees */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <Label htmlFor="purchaseTitleSearch" className="text-xs text-[#637888] uppercase tracking-wide">
                            Title Search
                          </Label>
                          <CurrencyInput
                            id="purchaseTitleSearch"
                            testId="input-purchase-title-search"
                            value={bridgeInputs.titleSearch}
                            onChange={(v) => updateBridgeInput('titleSearch', v)}
                            placeholder="275"
                          />
                        </div>
                        <div>
                          <Label htmlFor="purchaseLienSearch" className="text-xs text-[#637888] uppercase tracking-wide">
                            Lien Search
                          </Label>
                          <CurrencyInput
                            id="purchaseLienSearch"
                            testId="input-purchase-lien-search"
                            value={bridgeInputs.lienSearch}
                            onChange={(v) => updateBridgeInput('lienSearch', v)}
                            placeholder="200"
                          />
                        </div>
                        <div>
                          <Label htmlFor="purchaseTitleEndorsements" className="text-xs text-[#637888] uppercase tracking-wide">
                            Title Endorsements
                          </Label>
                          <CurrencyInput
                            id="purchaseTitleEndorsements"
                            testId="input-purchase-title-endorsements"
                            value={bridgeInputs.titleEndorsements}
                            onChange={(v) => updateBridgeInput('titleEndorsements', v)}
                            placeholder="375"
                          />
                        </div>
                        <div>
                          <Label htmlFor="purchaseWireFee" className="text-xs text-[#637888] uppercase tracking-wide">
                            Wire Fee
                          </Label>
                          <CurrencyInput
                            id="purchaseWireFee"
                            testId="input-purchase-wire-fee"
                            value={bridgeInputs.wireFee}
                            onChange={(v) => updateBridgeInput('wireFee', v)}
                            placeholder="35"
                          />
                        </div>
                      </div>

                      {/* Lender Due Diligence & Processing */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-slate-200">
                        <div>
                          <Label htmlFor="purchaseLenderDD" className="text-xs text-[#637888] uppercase tracking-wide">
                            Lender Due Diligence
                          </Label>
                          <CurrencyInput
                            id="purchaseLenderDD"
                            testId="input-purchase-lender-dd"
                            value={bridgeInputs.lenderDueDiligence}
                            onChange={(v) => updateBridgeInput('lenderDueDiligence', v)}
                            placeholder="5000"
                          />
                        </div>
                        <div>
                          <Label htmlFor="purchaseProcessingFee" className="text-xs text-[#637888] uppercase tracking-wide">
                            Processing Fee
                          </Label>
                          <CurrencyInput
                            id="purchaseProcessingFee"
                            testId="input-purchase-processing-fee"
                            value={bridgeInputs.processingFee}
                            onChange={(v) => updateBridgeInput('processingFee', v)}
                            placeholder="5000"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Summary Panel - Right Column */}
                <div className="lg:col-span-2">
                  <div className="sticky top-6">
                    <Card className="border border-slate-200 shadow-lg overflow-hidden">
                      {/* Yellow accent line */}
                      <div className="h-1 bg-[#FFFF00]" />
                      
                      {/* Summary Header */}
                      <div className="bg-[#002F87] text-white px-6 py-4">
                        <h2 className="font-heading font-bold text-sm uppercase tracking-wide flex items-center gap-2">
                          <CalcIcon className="w-4 h-4" strokeWidth={1.5} />
                          Bridge Loan Summary
                        </h2>
                        <p className="text-xs text-white/70 mt-1">Purchase</p>
                      </div>

                      <CardContent className="p-6 space-y-6">
                        {/* Key Metrics */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="col-span-2 p-4 bg-[#002F87] rounded-md">
                            <p className="text-xs text-white/80 uppercase tracking-wide mb-1">Loan Amount</p>
                            <p className="font-mono-number text-2xl font-bold text-white animate-number" data-testid="result-purchase-loan">
                              {formatCurrency(bridgeMetrics?.loanAmount || 0)}
                            </p>
                          </div>
                          <div className="p-3 bg-amber-50 rounded-md border border-amber-200">
                            <p className="text-xs text-amber-700 uppercase tracking-wide mb-1">Cash to Close</p>
                            <p className="font-mono-number text-lg font-semibold text-amber-700 animate-number" data-testid="result-purchase-cash-close">
                              {formatCurrency(bridgeMetrics?.cashToClose || 0)}
                            </p>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
                            <p className="text-xs text-[#637888] uppercase tracking-wide mb-1">Monthly Payment</p>
                            <p className="font-mono-number text-lg font-semibold text-[#002F87] animate-number">
                              {formatCurrency(bridgeMetrics?.monthlyPayment || 0)}
                            </p>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
                            <p className="text-xs text-[#637888] uppercase tracking-wide mb-1">Purchase LTV</p>
                            <p className="font-mono-number text-lg font-semibold text-[#002F87] animate-number">
                              {formatPercentage(bridgeMetrics?.purchaseLTV || 0)}
                            </p>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
                            <p className="text-xs text-[#637888] uppercase tracking-wide mb-1">Total Closing Costs</p>
                            <p className="font-mono-number text-lg font-semibold text-[#002F87] animate-number">
                              {formatCurrency(bridgeMetrics?.totalClosingCosts || 0)}
                            </p>
                          </div>
                        </div>

                        <Separator />

                        {/* Sources & Uses */}
                        <div>
                          <h3 className="font-heading font-semibold text-sm text-[#002F87] uppercase tracking-wide mb-3">
                            Sources & Uses
                          </h3>
                          
                          {/* Sources */}
                          <div className="mb-4">
                            <p className="text-xs text-[#637888] uppercase tracking-wide mb-2">Sources</p>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-600">Loan Amount</span>
                                <span className="font-mono-number text-slate-900">
                                  {formatCurrency(bridgeMetrics?.loanAmount || 0)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-600">Cash to Close</span>
                                <span className="font-mono-number text-slate-900">
                                  {formatCurrency(bridgeMetrics?.cashToClose || 0)}
                                </span>
                              </div>
                              <Separator className="my-2" />
                              <div className="flex justify-between font-semibold text-[#002F87]">
                                <span>Total Sources</span>
                                <span className="font-mono-number">
                                  {formatCurrency(bridgeMetrics?.totalSources || 0)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Uses */}
                          <div>
                            <p className="text-xs text-[#637888] uppercase tracking-wide mb-2">Uses</p>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-600">Purchase Price</span>
                                <span className="font-mono-number text-slate-900">
                                  {formatCurrency(bridgeMetrics?.purchasePrice || 0)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-600">Total Closing Costs</span>
                                <span className="font-mono-number text-slate-900">
                                  {formatCurrency(bridgeMetrics?.totalClosingCosts || 0)}
                                </span>
                              </div>
                              <Separator className="my-2" />
                              <div className="flex justify-between font-semibold text-[#002F87]">
                                <span>Total Uses</span>
                                <span className="font-mono-number">
                                  {formatCurrency(bridgeMetrics?.totalUses || 0)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <Separator />

                        {/* Closing Costs Breakdown */}
                        <div>
                          <h3 className="font-heading font-semibold text-sm text-[#002F87] uppercase tracking-wide mb-3">
                            Closing Costs Breakdown
                          </h3>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-600">Lender Origination ({formatPercentage(bridgeMetrics?.lenderOriginationFeePercent || 0)})</span>
                              <span className="font-mono-number text-slate-900">
                                {formatCurrency(bridgeMetrics?.lenderOriginationFee || 0)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Broker Origination ({formatPercentage(bridgeMetrics?.brokerOriginationFeePercent || 0)})</span>
                              <span className="font-mono-number text-slate-900">
                                {formatCurrency(bridgeMetrics?.brokerOriginationFee || 0)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Title/Doc Fees (calculated)</span>
                              <span className="font-mono-number text-slate-900">
                                {formatCurrency(bridgeMetrics?.totalPercentageFees || 0)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Loan Fees (fixed)</span>
                              <span className="font-mono-number text-slate-900">
                                {formatCurrency(bridgeMetrics?.totalFixedLoanFees || 0)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Due Diligence & Processing</span>
                              <span className="font-mono-number text-slate-900">
                                {formatCurrency(bridgeMetrics?.totalLenderFees || 0)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Interest Reserve</span>
                              <span className="font-mono-number text-slate-900">
                                {formatCurrency(bridgeMetrics?.interestReserve || 0)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Pre-Paid Interest</span>
                              <span className="font-mono-number text-slate-900">
                                {formatCurrency(bridgeMetrics?.prePaidInterest || 0)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <PDFDownloadLink
                          document={<BridgeLoanSummaryPDF data={bridgeMetrics} inputs={bridgeInputs} transactionType="purchase" loanOfficer={loanOfficer} />}
                          fileName={`bridge-purchase-summary-${bridgeInputs.projectName?.replace(/\s+/g, '-') || 'project'}-${new Date().toISOString().split('T')[0]}.pdf`}
                          className="block"
                        >
                          {({ loading }) => (
                            <Button
                              data-testid="btn-download-purchase-pdf"
                              className="w-full h-12 bg-[#FFFF00] hover:bg-[#E6E600] text-black font-heading font-semibold uppercase tracking-wide"
                              disabled={loading || !hasBridgeResults}
                            >
                              {loading ? (
                                <>Generating PDF...</>
                              ) : (
                                <>
                                  <Download className="w-4 h-4 mr-2" strokeWidth={2} />
                                  Download PDF Summary
                                </>
                              )}
                            </Button>
                          )}
                        </PDFDownloadLink>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            )}

            {/* Refinance Cash-Out Calculator */}
            {isRefinanceCashOut && (
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                {/* Input Sections - Left Column */}
                <div className="lg:col-span-3 space-y-6">
                  {/* Property Information */}
                  <Card className="border border-slate-200 shadow-sm">
                    <CardHeader className="pb-4">
                      <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                        <Building2 className="w-4 h-4" strokeWidth={1.5} />
                        Property Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <Label htmlFor="bridgeProjectName" className="text-xs text-[#637888] uppercase tracking-wide">
                            Project Name
                          </Label>
                          <Input
                            id="bridgeProjectName"
                            data-testid="input-bridge-project-name"
                            value={bridgeInputs.projectName}
                            onChange={(e) => updateBridgeInput('projectName', e.target.value)}
                            placeholder="Enter project name"
                            className="mt-1"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label htmlFor="bridgePropertyAddress" className="text-xs text-[#637888] uppercase tracking-wide">
                            Property Address
                          </Label>
                          <Input
                            id="bridgePropertyAddress"
                            data-testid="input-bridge-property-address"
                            value={bridgeInputs.propertyAddress}
                            onChange={(e) => updateBridgeInput('propertyAddress', e.target.value)}
                            placeholder="Enter property address"
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Property Value & Loan Amount */}
                  <Card className="border border-slate-200 shadow-sm">
                    <CardHeader className="pb-4">
                      <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                        <DollarSign className="w-4 h-4" strokeWidth={1.5} />
                        Property Value & Loan
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="propertyValue" className="text-xs text-[#637888] uppercase tracking-wide">
                            Property Value
                          </Label>
                          <CurrencyInput
                            id="propertyValue"
                            testId="input-bridge-property-value"
                            value={bridgeInputs.propertyValue}
                            onChange={(v) => updateBridgeInput('propertyValue', v)}
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <Label htmlFor="bridgeLoanAmount" className="text-xs text-[#637888] uppercase tracking-wide">
                            Loan Amount
                          </Label>
                          <CurrencyInput
                            id="bridgeLoanAmount"
                            testId="input-bridge-loan-amount"
                            value={bridgeInputs.loanAmount}
                            onChange={(v) => updateBridgeInput('loanAmount', v)}
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-[#637888] uppercase tracking-wide">
                            Loan-to-Value (LTV)
                          </Label>
                          <div className="mt-1 p-2 bg-[#EAF5FD] border border-slate-200 rounded-md">
                            <span className="font-mono-number text-[#002F87] font-semibold" data-testid="display-bridge-ltv">
                              {formatPercentage(bridgeMetrics?.ltv || 0)}
                            </span>
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="payoffAmount" className="text-xs text-[#637888] uppercase tracking-wide">
                            Existing Loan Payoff
                          </Label>
                          <CurrencyInput
                            id="payoffAmount"
                            testId="input-bridge-payoff-amount"
                            value={bridgeInputs.payoffAmount}
                            onChange={(v) => updateBridgeInput('payoffAmount', v)}
                            placeholder="0"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Loan Structure */}
                  <Card className="border border-slate-200 shadow-sm">
                    <CardHeader className="pb-4">
                      <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                        <Landmark className="w-4 h-4" strokeWidth={1.5} />
                        Loan Structure
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="bridgeLoanTerm" className="text-xs text-[#637888] uppercase tracking-wide">
                            Loan Term (months)
                          </Label>
                          <Input
                            id="bridgeLoanTerm"
                            data-testid="input-bridge-loan-term"
                            type="number"
                            value={bridgeInputs.loanTerm}
                            onChange={(e) => updateBridgeInput('loanTerm', e.target.value)}
                            placeholder="e.g., 12"
                            className="mt-1 font-mono-number"
                          />
                        </div>
                        <div>
                          <Label htmlFor="bridgeInterestRate" className="text-xs text-[#637888] uppercase tracking-wide">
                            Interest Rate
                          </Label>
                          <div className="flex gap-2 mt-1">
                            <div className="flex-1">
                              <PercentageInput
                                id="bridgeInterestRate"
                                testId="input-bridge-interest-rate"
                                value={bridgeInputs.interestRate}
                                onChange={(v) => updateBridgeInput('interestRate', v)}
                                placeholder="e.g., 10"
                              />
                            </div>
                            <Select
                              value={bridgeInputs.rateType}
                              onValueChange={(v) => updateBridgeInput('rateType', v)}
                            >
                              <SelectTrigger data-testid="select-bridge-rate-type" className="w-28">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Variable">Variable</SelectItem>
                                <SelectItem value="Fixed">Fixed</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="bridgeInterestReserveMonths" className="text-xs text-[#637888] uppercase tracking-wide">
                            Interest Reserve (Months)
                          </Label>
                          <Select
                            value={bridgeInputs.interestReserveMonths}
                            onValueChange={(v) => updateBridgeInput('interestReserveMonths', v)}
                          >
                            <SelectTrigger data-testid="select-bridge-interest-reserve-months" className="mt-1">
                              <SelectValue placeholder="Select months" />
                            </SelectTrigger>
                            <SelectContent>
                              {INTEREST_RESERVE_OPTIONS.map((months) => (
                                <SelectItem key={months} value={String(months)}>
                                  {months} {months === 1 ? 'month' : 'months'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="prePaidInterestDays" className="text-xs text-[#637888] uppercase tracking-wide">
                            Pre-Paid Interest (Days)
                          </Label>
                          <Select
                            value={bridgeInputs.prePaidInterestDays}
                            onValueChange={(v) => updateBridgeInput('prePaidInterestDays', v)}
                          >
                            <SelectTrigger data-testid="select-bridge-prepaid-days" className="mt-1">
                              <SelectValue placeholder="Select days" />
                            </SelectTrigger>
                            <SelectContent>
                              {PREPAID_INTEREST_DAYS_OPTIONS.map((days) => (
                                <SelectItem key={days} value={String(days)}>
                                  {days} days
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-[#637888] uppercase tracking-wide">
                            Monthly Payment (IO)
                          </Label>
                          <div className="mt-1 p-2 bg-[#EAF5FD] border border-slate-200 rounded-md">
                            <span className="font-mono-number text-[#002F87] font-semibold" data-testid="display-bridge-monthly-payment">
                              {formatCurrency(bridgeMetrics?.monthlyPayment || 0)}
                            </span>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-[#637888] uppercase tracking-wide">
                            Interest Reserve Amount
                          </Label>
                          <div className="mt-1 p-2 bg-[#EAF5FD] border border-slate-200 rounded-md">
                            <span className="font-mono-number text-[#002F87] font-semibold" data-testid="display-bridge-interest-reserve">
                              {formatCurrency(bridgeMetrics?.interestReserve || 0)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Origination Fees */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-200">
                        <div>
                          <Label htmlFor="refinanceLenderOrigination" className="text-xs text-[#637888] uppercase tracking-wide">
                            Lender Origination Fee (%)
                          </Label>
                          <PercentageInput
                            id="refinanceLenderOrigination"
                            testId="input-refinance-lender-origination-percent"
                            value={bridgeInputs.lenderOriginationFeePercent}
                            onChange={(v) => updateBridgeInput('lenderOriginationFeePercent', v)}
                            placeholder="2"
                          />
                          <div className="mt-2 p-2 bg-[#EAF5FD] border border-slate-200 rounded-md">
                            <span className="text-xs text-[#637888] uppercase tracking-wide">Calculated: </span>
                            <span className="font-mono-number text-[#002F87] font-semibold" data-testid="display-refinance-lender-origination">
                              {formatCurrency(bridgeMetrics?.lenderOriginationFee || 0)}
                            </span>
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="refinanceBrokerOrigination" className="text-xs text-[#637888] uppercase tracking-wide">
                            Broker Origination Fee (%)
                          </Label>
                          <PercentageInput
                            id="refinanceBrokerOrigination"
                            testId="input-refinance-broker-origination-percent"
                            value={bridgeInputs.brokerOriginationFeePercent}
                            onChange={(v) => updateBridgeInput('brokerOriginationFeePercent', v)}
                            placeholder="2"
                          />
                          <div className="mt-2 p-2 bg-[#EAF5FD] border border-slate-200 rounded-md">
                            <span className="text-xs text-[#637888] uppercase tracking-wide">Calculated: </span>
                            <span className="font-mono-number text-[#002F87] font-semibold" data-testid="display-refinance-broker-origination">
                              {formatCurrency(bridgeMetrics?.brokerOriginationFee || 0)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Loan Fees - Refinance */}
                  <Card className="border border-slate-200 shadow-sm">
                    <CardHeader className="pb-4">
                      <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                        <Settings className="w-4 h-4" strokeWidth={1.5} />
                        Loan Fees
                      </CardTitle>
                      <p className="text-xs text-slate-500 mt-1">Percentage-based fees auto-calculated from loan amount</p>
                    </CardHeader>
                    <CardContent>
                      {/* Auto-calculated percentage fees */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-3 bg-slate-50 rounded-lg">
                        <div>
                          <Label className="text-xs text-[#637888] uppercase tracking-wide">
                            Lender's Title Policy (0.081%)
                          </Label>
                          <div className="mt-1 p-2 bg-white border border-slate-200 rounded-md">
                            <span className="font-mono-number text-slate-700">
                              {formatCurrency(bridgeMetrics?.lendersTitlePolicy || 0)}
                            </span>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-[#637888] uppercase tracking-wide">
                            Documentary Stamps (0.35%)
                          </Label>
                          <div className="mt-1 p-2 bg-white border border-slate-200 rounded-md">
                            <span className="font-mono-number text-slate-700">
                              {formatCurrency(bridgeMetrics?.documentaryStamps || 0)}
                            </span>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-[#637888] uppercase tracking-wide">
                            Intangible Stamps (0.20%)
                          </Label>
                          <div className="mt-1 p-2 bg-white border border-slate-200 rounded-md">
                            <span className="font-mono-number text-slate-700">
                              {formatCurrency(bridgeMetrics?.intangibleTax || 0)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Editable fixed fees */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <Label htmlFor="refinanceTitleSearch" className="text-xs text-[#637888] uppercase tracking-wide">
                            Title Search
                          </Label>
                          <CurrencyInput
                            id="refinanceTitleSearch"
                            testId="input-refinance-title-search"
                            value={bridgeInputs.titleSearch}
                            onChange={(v) => updateBridgeInput('titleSearch', v)}
                            placeholder="275"
                          />
                        </div>
                        <div>
                          <Label htmlFor="refinanceLienSearch" className="text-xs text-[#637888] uppercase tracking-wide">
                            Lien Search
                          </Label>
                          <CurrencyInput
                            id="refinanceLienSearch"
                            testId="input-refinance-lien-search"
                            value={bridgeInputs.lienSearch}
                            onChange={(v) => updateBridgeInput('lienSearch', v)}
                            placeholder="200"
                          />
                        </div>
                        <div>
                          <Label htmlFor="refinanceTitleEndorsements" className="text-xs text-[#637888] uppercase tracking-wide">
                            Title Endorsements
                          </Label>
                          <CurrencyInput
                            id="refinanceTitleEndorsements"
                            testId="input-refinance-title-endorsements"
                            value={bridgeInputs.titleEndorsements}
                            onChange={(v) => updateBridgeInput('titleEndorsements', v)}
                            placeholder="375"
                          />
                        </div>
                        <div>
                          <Label htmlFor="refinanceWireFee" className="text-xs text-[#637888] uppercase tracking-wide">
                            Wire Fee
                          </Label>
                          <CurrencyInput
                            id="refinanceWireFee"
                            testId="input-refinance-wire-fee"
                            value={bridgeInputs.wireFee}
                            onChange={(v) => updateBridgeInput('wireFee', v)}
                            placeholder="35"
                          />
                        </div>
                      </div>

                      {/* Lender Due Diligence & Processing */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-slate-200">
                        <div>
                          <Label htmlFor="refinanceLenderDD" className="text-xs text-[#637888] uppercase tracking-wide">
                            Lender Due Diligence
                          </Label>
                          <CurrencyInput
                            id="refinanceLenderDD"
                            testId="input-refinance-lender-dd"
                            value={bridgeInputs.lenderDueDiligence}
                            onChange={(v) => updateBridgeInput('lenderDueDiligence', v)}
                            placeholder="5000"
                          />
                        </div>
                        <div>
                          <Label htmlFor="refinanceProcessingFee" className="text-xs text-[#637888] uppercase tracking-wide">
                            Processing Fee
                          </Label>
                          <CurrencyInput
                            id="refinanceProcessingFee"
                            testId="input-refinance-processing-fee"
                            value={bridgeInputs.processingFee}
                            onChange={(v) => updateBridgeInput('processingFee', v)}
                            placeholder="5000"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Summary Panel - Right Column */}
                <div className="lg:col-span-2">
                  <div className="sticky top-6">
                    <Card className="border border-slate-200 shadow-lg overflow-hidden">
                      {/* Yellow accent line */}
                      <div className="h-1 bg-[#FFFF00]" />
                      
                      {/* Summary Header */}
                      <div className="bg-[#002F87] text-white px-6 py-4">
                        <h2 className="font-heading font-bold text-sm uppercase tracking-wide flex items-center gap-2">
                          <CalcIcon className="w-4 h-4" strokeWidth={1.5} />
                          Bridge Loan Summary
                        </h2>
                        <p className="text-xs text-white/70 mt-1">Refinance Cash-Out</p>
                      </div>

                      <CardContent className="p-6 space-y-6">
                        {/* Key Metrics */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="col-span-2 p-4 bg-[#002F87] rounded-md">
                            <p className="text-xs text-white/80 uppercase tracking-wide mb-1">Loan Amount</p>
                            <p className="font-mono-number text-2xl font-bold text-white animate-number" data-testid="result-bridge-loan-amount">
                              {formatCurrency(bridgeMetrics?.loanAmount || 0)}
                            </p>
                          </div>
                          <div className="p-3 bg-green-50 rounded-md border border-green-200">
                            <p className="text-xs text-green-700 uppercase tracking-wide mb-1">Cash Out</p>
                            <p className="font-mono-number text-lg font-semibold text-green-700 animate-number" data-testid="result-bridge-cash-out">
                              {formatCurrency(bridgeMetrics?.cashOut || 0)}
                            </p>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
                            <p className="text-xs text-[#637888] uppercase tracking-wide mb-1">Monthly Payment</p>
                            <p className="font-mono-number text-lg font-semibold text-[#002F87] animate-number" data-testid="result-bridge-monthly">
                              {formatCurrency(bridgeMetrics?.monthlyPayment || 0)}
                            </p>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
                            <p className="text-xs text-[#637888] uppercase tracking-wide mb-1">Loan-to-Value</p>
                            <p className="font-mono-number text-lg font-semibold text-[#002F87] animate-number" data-testid="result-bridge-ltv">
                              {formatPercentage(bridgeMetrics?.ltv || 0)}
                            </p>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
                            <p className="text-xs text-[#637888] uppercase tracking-wide mb-1">Total Closing Costs</p>
                            <p className="font-mono-number text-lg font-semibold text-[#002F87] animate-number" data-testid="result-bridge-closing-costs">
                              {formatCurrency(bridgeMetrics?.totalClosingCosts || 0)}
                            </p>
                          </div>
                        </div>

                        <Separator />

                        {/* Sources & Uses */}
                        <div>
                          <h3 className="font-heading font-semibold text-sm text-[#002F87] uppercase tracking-wide mb-3">
                            Sources & Uses
                          </h3>
                          
                          {/* Sources */}
                          <div className="mb-4">
                            <p className="text-xs text-[#637888] uppercase tracking-wide mb-2">Sources</p>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-600">Loan Amount</span>
                                <span className="font-mono-number text-slate-900" data-testid="source-bridge-loan">
                                  {formatCurrency(bridgeMetrics?.loanAmount || 0)}
                                </span>
                              </div>
                              {(bridgeMetrics?.equityRequired || 0) > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Borrower Equity</span>
                                  <span className="font-mono-number text-slate-900">
                                    {formatCurrency(bridgeMetrics?.equityRequired || 0)}
                                  </span>
                                </div>
                              )}
                              <Separator className="my-2" />
                              <div className="flex justify-between font-semibold text-[#002F87]">
                                <span>Total Sources</span>
                                <span className="font-mono-number" data-testid="total-bridge-sources">
                                  {formatCurrency((bridgeMetrics?.totalSources || 0) + (bridgeMetrics?.equityRequired || 0))}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Uses */}
                          <div>
                            <p className="text-xs text-[#637888] uppercase tracking-wide mb-2">Uses</p>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-600">Existing Loan Payoff</span>
                                <span className="font-mono-number text-slate-900" data-testid="use-bridge-payoff">
                                  {formatCurrency(bridgeMetrics?.payoffAmount || 0)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-600">Total Closing Costs</span>
                                <span className="font-mono-number text-slate-900">
                                  {formatCurrency(bridgeMetrics?.totalClosingCosts || 0)}
                                </span>
                              </div>
                              <div className="flex justify-between text-green-700">
                                <span>Cash Out to Borrower</span>
                                <span className="font-mono-number" data-testid="use-bridge-cashout">
                                  {formatCurrency(bridgeMetrics?.cashOut || 0)}
                                </span>
                              </div>
                              <Separator className="my-2" />
                              <div className="flex justify-between font-semibold text-[#002F87]">
                                <span>Total Uses</span>
                                <span className="font-mono-number" data-testid="total-bridge-uses">
                                  {formatCurrency(bridgeMetrics?.totalUses || 0)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <Separator />

                        {/* Closing Costs Breakdown */}
                        <div>
                          <h3 className="font-heading font-semibold text-sm text-[#002F87] uppercase tracking-wide mb-3">
                            Closing Costs Breakdown
                          </h3>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-600">Lender Origination ({formatPercentage(bridgeMetrics?.lenderOriginationFeePercent || 0)})</span>
                              <span className="font-mono-number text-slate-900">
                                {formatCurrency(bridgeMetrics?.lenderOriginationFee || 0)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Broker Origination ({formatPercentage(bridgeMetrics?.brokerOriginationFeePercent || 0)})</span>
                              <span className="font-mono-number text-slate-900">
                                {formatCurrency(bridgeMetrics?.brokerOriginationFee || 0)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Title/Doc Fees (calculated)</span>
                              <span className="font-mono-number text-slate-900">
                                {formatCurrency(bridgeMetrics?.totalPercentageFees || 0)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Loan Fees (fixed)</span>
                              <span className="font-mono-number text-slate-900">
                                {formatCurrency(bridgeMetrics?.totalFixedLoanFees || 0)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Due Diligence & Processing</span>
                              <span className="font-mono-number text-slate-900">
                                {formatCurrency(bridgeMetrics?.totalLenderFees || 0)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Interest Reserve</span>
                              <span className="font-mono-number text-slate-900">
                                {formatCurrency(bridgeMetrics?.interestReserve || 0)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Pre-Paid Interest ({bridgeMetrics?.prePaidInterestDays || 15} days)</span>
                              <span className="font-mono-number text-slate-900">
                                {formatCurrency(bridgeMetrics?.prePaidInterest || 0)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Fee Rate Note */}
                        <div className="p-3 bg-[#EAF5FD] rounded-md border-l-4 border-[#FFFF00]">
                          <p className="text-xs text-[#637888]">
                            <span className="font-semibold text-[#002F87]">Note:</span> Title/doc fees auto-calculated: Lender's Title (0.081%), Doc Stamps (0.35%), Intangible (0.20%). Owner's Title (0.42%) applies to Purchase only.
                          </p>
                        </div>

                        {/* PDF Download Button */}
                        <PDFDownloadLink
                          document={<BridgeLoanSummaryPDF data={bridgeMetrics} inputs={bridgeInputs} loanOfficer={loanOfficer} />}
                          fileName={`bridge-loan-summary-${bridgeInputs.projectName?.replace(/\s+/g, '-') || 'project'}-${new Date().toISOString().split('T')[0]}.pdf`}
                          className="block"
                        >
                          {({ loading }) => (
                            <Button
                              data-testid="btn-download-bridge-pdf"
                              className="w-full h-12 bg-[#FFFF00] hover:bg-[#E6E600] text-black font-heading font-semibold uppercase tracking-wide"
                              disabled={loading || !hasBridgeResults}
                            >
                              {loading ? (
                                <>Generating PDF...</>
                              ) : (
                                <>
                                  <Download className="w-4 h-4 mr-2" strokeWidth={2} />
                                  Download PDF Summary
                                </>
                              )}
                            </Button>
                          )}
                        </PDFDownloadLink>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Construction Loan Calculator — Spec Construction Loan Sizer */}
        {isConstructionLoan && (
          <LoanSizer loanOfficer={loanOfficer} controlsRef={sizerControlsRef} />
        )}

        {/* Portfolio/Developer Loan Calculator */}
        {isPortfolioLoan && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Input Sections - Left Column */}
            <div className="lg:col-span-3 space-y-6">
              {/* Transaction Type Selection */}
              <Card className="border border-slate-200 shadow-sm bg-gradient-to-r from-slate-50 to-white">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                    <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
                    Transaction Type
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div>
                    <Label className="text-xs text-[#637888] uppercase tracking-wide">
                      Select Transaction Type <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={portfolioInputs.transactionType}
                      onValueChange={(v) => updatePortfolioInput('transactionType', v)}
                    >
                      <SelectTrigger data-testid="select-portfolio-transaction-type" className="mt-1">
                        <SelectValue placeholder="Select transaction type..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={PORTFOLIO_TRANSACTION_TYPES.DEVELOPER}>
                          <div className="flex items-center gap-2">
                            <Construction className="w-4 h-4" />
                            Developer
                          </div>
                        </SelectItem>
                        <SelectItem value={PORTFOLIO_TRANSACTION_TYPES.REFINANCE_CASHOUT}>
                          <div className="flex items-center gap-2">
                            <RefreshCw className="w-4 h-4" />
                            Refinance Cash-Out
                          </div>
                        </SelectItem>
                        <SelectItem value={PORTFOLIO_TRANSACTION_TYPES.PURCHASE}>
                          <div className="flex items-center gap-2">
                            <Banknote className="w-4 h-4" />
                            Purchase
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Show remaining fields only after Transaction Type is selected */}
              {!portfolioInputs.transactionType ? (
                /* Placeholder message when no transaction type selected */
                <Card className="border border-dashed border-slate-300 shadow-sm bg-slate-50/50">
                  <CardContent className="py-12">
                    <div className="text-center space-y-3">
                      <div className="w-16 h-16 mx-auto bg-slate-100 rounded-full flex items-center justify-center">
                        <RefreshCw className="w-8 h-8 text-slate-400" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-lg font-heading font-semibold text-slate-600">
                        Select a Transaction Type
                      </h3>
                      <p className="text-sm text-slate-500 max-w-sm mx-auto">
                        Choose Developer, Refinance Cash-Out, or Purchase above to display the loan calculator fields.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
              <>
              {/* Property Information */}
              <Card className="border border-slate-200 shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                    <Building2 className="w-4 h-4" strokeWidth={1.5} />
                    Property Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label htmlFor="portfolioProjectName" className="text-xs text-[#637888] uppercase tracking-wide">
                        Project Name
                      </Label>
                      <Input
                        id="portfolioProjectName"
                        data-testid="input-portfolio-project-name"
                        value={portfolioInputs.projectName}
                        onChange={(e) => updatePortfolioInput('projectName', e.target.value)}
                        placeholder="Enter project name"
                        className="mt-1"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="portfolioPropertyAddress" className="text-xs text-[#637888] uppercase tracking-wide">
                        Property Address
                      </Label>
                      <Input
                        id="portfolioPropertyAddress"
                        data-testid="input-portfolio-property-address"
                        value={portfolioInputs.propertyAddress}
                        onChange={(e) => updatePortfolioInput('propertyAddress', e.target.value)}
                        placeholder="Enter property address"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Purchase Price & Loan / Property Value & Loan */}
              <Card className="border border-slate-200 shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                    <DollarSign className="w-4 h-4" strokeWidth={1.5} />
                    {portfolioInputs.transactionType === PORTFOLIO_TRANSACTION_TYPES.REFINANCE_CASHOUT 
                      ? 'Property Value & Loan' 
                      : 'Purchase Price & Loan'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="portfolioPurchasePrice" className="text-xs text-[#637888] uppercase tracking-wide">
                        {portfolioInputs.transactionType === PORTFOLIO_TRANSACTION_TYPES.REFINANCE_CASHOUT 
                          ? 'Property Value' 
                          : 'Purchase Price'}
                      </Label>
                      <CurrencyInput
                        id="portfolioPurchasePrice"
                        testId="input-portfolio-purchase-price"
                        value={portfolioInputs.purchasePrice}
                        onChange={(v) => updatePortfolioInput('purchasePrice', v)}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <Label htmlFor="portfolioTargetLTV" className="text-xs text-[#637888] uppercase tracking-wide">
                        Target LTV (%)
                      </Label>
                      <PercentageInput
                        id="portfolioTargetLTV"
                        testId="input-portfolio-ltv"
                        value={portfolioInputs.targetLTV}
                        onChange={(v) => updatePortfolioInput('targetLTV', v)}
                        placeholder="75"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-[#637888] uppercase tracking-wide">
                        Loan Amount (Calculated)
                      </Label>
                      <div className="mt-1 p-2 bg-[#EAF5FD] border border-slate-200 rounded-md">
                        <span className="font-mono-number text-[#002F87] font-semibold" data-testid="display-portfolio-loan-amount">
                          {formatCurrency(portfolioMetrics?.loanAmount || 0)}
                        </span>
                      </div>
                    </div>
                    {portfolioInputs.transactionType === PORTFOLIO_TRANSACTION_TYPES.REFINANCE_CASHOUT ? (
                      <div>
                        <Label htmlFor="existingMortgageBalance" className="text-xs text-[#637888] uppercase tracking-wide">
                          Existing Mortgage Balance
                        </Label>
                        <CurrencyInput
                          id="existingMortgageBalance"
                          testId="input-portfolio-existing-mortgage"
                          value={portfolioInputs.existingMortgageBalance}
                          onChange={(v) => updatePortfolioInput('existingMortgageBalance', v)}
                          placeholder="0"
                        />
                      </div>
                    ) : (
                      <div>
                        <Label className="text-xs text-[#637888] uppercase tracking-wide">
                          Down Payment
                        </Label>
                        <div className="mt-1 p-2 bg-slate-50 border border-slate-200 rounded-md">
                          <span className="font-mono-number text-slate-700 font-semibold" data-testid="display-portfolio-down-payment">
                            {formatCurrency(portfolioMetrics?.downPayment || 0)}
                          </span>
                        </div>
                      </div>
                    )}
                    {portfolioInputs.transactionType === PORTFOLIO_TRANSACTION_TYPES.REFINANCE_CASHOUT && (
                      <div>
                        <Label className="text-xs text-[#637888] uppercase tracking-wide">
                          Cash Out Amount
                        </Label>
                        <div className="mt-1 p-2 bg-emerald-50 border border-emerald-200 rounded-md">
                          <span className="font-mono-number text-emerald-700 font-semibold" data-testid="display-portfolio-cash-out">
                            {formatCurrency(portfolioMetrics?.cashOutAmount || 0)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Loan Structure */}
              <Card className="border border-slate-200 shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                    <Landmark className="w-4 h-4" strokeWidth={1.5} />
                    Loan Structure
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="portfolioInterestRate" className="text-xs text-[#637888] uppercase tracking-wide">
                        Interest Rate
                      </Label>
                      <div className="flex gap-2 mt-1">
                        <div className="flex-1">
                          <PercentageInput
                            id="portfolioInterestRate"
                            testId="input-portfolio-interest-rate"
                            value={portfolioInputs.interestRate}
                            onChange={(v) => updatePortfolioInput('interestRate', v)}
                            placeholder="e.g., 7.5"
                          />
                        </div>
                        <Select
                          value={portfolioInputs.rateType}
                          onValueChange={(v) => updatePortfolioInput('rateType', v)}
                        >
                          <SelectTrigger data-testid="select-portfolio-rate-type" className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="3/1 ARM">3/1 ARM</SelectItem>
                            <SelectItem value="5/1 ARM">5/1 ARM</SelectItem>
                            <SelectItem value="7/1 ARM">7/1 ARM</SelectItem>
                            <SelectItem value="Fixed">Fixed</SelectItem>
                            <SelectItem value="Variable">Variable</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="amortizationTerm" className="text-xs text-[#637888] uppercase tracking-wide">
                        Amortization Term (years)
                      </Label>
                      <Select
                        value={portfolioInputs.amortizationTerm}
                        onValueChange={(v) => updatePortfolioInput('amortizationTerm', v)}
                      >
                        <SelectTrigger data-testid="select-portfolio-amort-term" className="mt-1">
                          <SelectValue placeholder="Select term" />
                        </SelectTrigger>
                        <SelectContent>
                          {AMORTIZATION_TERM_OPTIONS.map((years) => (
                            <SelectItem key={years} value={String(years)}>
                              {years} years
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-[#637888] uppercase tracking-wide">
                        Interest-Only Payment (IO)
                      </Label>
                      <div className="mt-1 p-2 bg-blue-50 border border-blue-200 rounded-md">
                        <span className="font-mono-number text-blue-700 font-semibold" data-testid="display-portfolio-io">
                          {formatCurrency(portfolioMetrics?.ioPayment || 0)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-[#637888] uppercase tracking-wide">
                        Principal & Interest (P&I)
                      </Label>
                      <div className="mt-1 p-2 bg-green-50 border border-green-200 rounded-md">
                        <span className="font-mono-number text-green-700 font-semibold" data-testid="display-portfolio-pi">
                          {formatCurrency(portfolioMetrics?.piPayment || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Monthly Costs */}
              <Card className="border border-slate-200 shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                    <Receipt className="w-4 h-4" strokeWidth={1.5} />
                    Monthly Costs (Annual Amounts)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="portfolioTaxes" className="text-xs text-[#637888] uppercase tracking-wide">
                        Property Taxes (Annual)
                      </Label>
                      <CurrencyInput
                        id="portfolioTaxes"
                        testId="input-portfolio-taxes"
                        value={portfolioInputs.taxes}
                        onChange={(v) => updatePortfolioInput('taxes', v)}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <Label htmlFor="portfolioInsurance" className="text-xs text-[#637888] uppercase tracking-wide">
                        Insurance (Annual)
                      </Label>
                      <CurrencyInput
                        id="portfolioInsurance"
                        testId="input-portfolio-insurance"
                        value={portfolioInputs.insurance}
                        onChange={(v) => updatePortfolioInput('insurance', v)}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <Label htmlFor="portfolioHOA" className="text-xs text-[#637888] uppercase tracking-wide">
                        HOA (Monthly)
                      </Label>
                      <CurrencyInput
                        id="portfolioHOA"
                        testId="input-portfolio-hoa"
                        value={portfolioInputs.hoa}
                        onChange={(v) => updatePortfolioInput('hoa', v)}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Closing Costs */}
              <Card className="border border-slate-200 shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                    <Settings className="w-4 h-4" strokeWidth={1.5} />
                    Lender Fees
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="originationPoints" className="text-xs text-[#637888] uppercase tracking-wide">
                        Origination Points (%)
                      </Label>
                      <PercentageInput
                        id="originationPoints"
                        testId="input-portfolio-origination"
                        value={portfolioInputs.originationPoints}
                        onChange={(v) => updatePortfolioInput('originationPoints', v)}
                        placeholder="0.75"
                      />
                    </div>
                    <div>
                      <Label htmlFor="appraisalFee" className="text-xs text-[#637888] uppercase tracking-wide">
                        Appraisal Fee
                      </Label>
                      <CurrencyInput
                        id="appraisalFee"
                        testId="input-portfolio-appraisal"
                        value={portfolioInputs.appraisalFee}
                        onChange={(v) => updatePortfolioInput('appraisalFee', v)}
                        placeholder="3500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="portfolioProcessingFee" className="text-xs text-[#637888] uppercase tracking-wide">
                        Processing Fee
                      </Label>
                      <CurrencyInput
                        id="portfolioProcessingFee"
                        testId="input-portfolio-processing"
                        value={portfolioInputs.processingFee}
                        onChange={(v) => updatePortfolioInput('processingFee', v)}
                        placeholder="750"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Developer Costs - Only show for Developer transaction type */}
              {portfolioInputs.transactionType === PORTFOLIO_TRANSACTION_TYPES.DEVELOPER && (
              <Card className="border border-slate-200 shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                    <FileStack className="w-4 h-4" strokeWidth={1.5} />
                    Developer Costs
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="developerFeePercent" className="text-xs text-[#637888] uppercase tracking-wide">
                        Developer Fee (% of Purchase Price)
                      </Label>
                      <PercentageInput
                        id="developerFeePercent"
                        testId="input-portfolio-dev-fee-pct"
                        value={portfolioInputs.developerFeePercent}
                        onChange={(v) => updatePortfolioInput('developerFeePercent', v)}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-[#637888] uppercase tracking-wide">
                        Developer Fee Amount
                      </Label>
                      <div className="mt-1 p-2 bg-slate-50 border border-slate-200 rounded-md">
                        <span className="font-mono-number text-slate-700 font-semibold">
                          {formatCurrency(portfolioMetrics?.developerFee || 0)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="capitalContributionMonths" className="text-xs text-[#637888] uppercase tracking-wide">
                        Capital Contribution (Months of HOA)
                      </Label>
                      <Input
                        id="capitalContributionMonths"
                        data-testid="input-portfolio-cap-months"
                        type="number"
                        value={portfolioInputs.capitalContributionMonths}
                        onChange={(e) => updatePortfolioInput('capitalContributionMonths', e.target.value)}
                        placeholder="0"
                        className="mt-1 font-mono-number"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-[#637888] uppercase tracking-wide">
                        Capital Contribution Amount
                      </Label>
                      <div className="mt-1 p-2 bg-slate-50 border border-slate-200 rounded-md">
                        <span className="font-mono-number text-slate-700 font-semibold">
                          {formatCurrency(portfolioMetrics?.capitalContribution || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-[#637888] mt-3 italic">
                    Note: Deed Tax is typically included in developer fees and is not charged separately.
                  </p>
                </CardContent>
              </Card>
              )}

              {/* Deposit & Credits - Developer Sales (Not for Refinance Cash-Out) */}
              {portfolioInputs.transactionType !== PORTFOLIO_TRANSACTION_TYPES.REFINANCE_CASHOUT && (
              <Card className="border border-slate-200 shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                    <CreditCard className="w-4 h-4" strokeWidth={1.5} />
                    Deposit & Credits
                  </CardTitle>
                  <p className="text-xs text-slate-500 mt-1">For developer sales: buyer deposit and credits applied to reduce cash to close</p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="depositPercent" className="text-xs text-[#637888] uppercase tracking-wide">
                        Deposit % (of Purchase Price)
                      </Label>
                      <PercentageInput
                        id="depositPercent"
                        testId="input-portfolio-deposit-pct"
                        value={portfolioInputs.depositPercent}
                        onChange={(v) => updatePortfolioInput('depositPercent', v)}
                        placeholder="40"
                      />
                      <p className="text-xs text-slate-500 mt-1">Usually 40% of purchase price</p>
                    </div>
                    <div>
                      <Label className="text-xs text-[#637888] uppercase tracking-wide">
                        Deposit Amount
                      </Label>
                      <div className="mt-1 p-2 bg-emerald-50 border border-emerald-200 rounded-md">
                        <span className="font-mono-number text-emerald-700 font-semibold">
                          {formatCurrency(portfolioMetrics?.depositAmount || 0)}
                        </span>
                      </div>
                    </div>
                    {portfolioInputs.transactionType === PORTFOLIO_TRANSACTION_TYPES.DEVELOPER && (
                    <div>
                      <Label htmlFor="developerCredit" className="text-xs text-[#637888] uppercase tracking-wide">
                        Developer Credit
                      </Label>
                      <CurrencyInput
                        id="developerCredit"
                        testId="input-portfolio-dev-credit"
                        value={portfolioInputs.developerCredit}
                        onChange={(v) => updatePortfolioInput('developerCredit', v)}
                        placeholder="0"
                      />
                    </div>
                    )}
                    <div>
                      <Label htmlFor="lenderCredit" className="text-xs text-[#637888] uppercase tracking-wide">
                        Lender Credit
                      </Label>
                      <CurrencyInput
                        id="lenderCredit"
                        testId="input-portfolio-lender-credit"
                        value={portfolioInputs.lenderCredit}
                        onChange={(v) => updatePortfolioInput('lenderCredit', v)}
                        placeholder="0"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs text-[#637888] uppercase tracking-wide">
                        Total Credits
                      </Label>
                      <div className="mt-1 p-2 bg-emerald-50 border border-emerald-200 rounded-md">
                        <span className="font-mono-number text-emerald-700 font-semibold">
                          {formatCurrency(portfolioMetrics?.totalCredits || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              )}

              {/* Settlement Charges - Auto-Calculated */}
              <Card className="border border-slate-200 shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                    <Receipt className="w-4 h-4" strokeWidth={1.5} />
                    Settlement Charges (Auto-Calculated)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="flex justify-between p-2 bg-slate-50 rounded">
                      <span className="text-slate-600">Lender's Title Policy (0.081% of loan)</span>
                      <span className="font-mono-number text-slate-900">{formatCurrency(portfolioMetrics?.lendersTitlePolicy || 0)}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-slate-50 rounded">
                      <span className="text-slate-600">Owner's Title Insurance (0.42% of price)</span>
                      <span className="font-mono-number text-slate-900">{formatCurrency(portfolioMetrics?.ownersTitleInsurance || 0)}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-slate-50 rounded">
                      <span className="text-slate-600">Documentary Stamps (0.35% of loan)</span>
                      <span className="font-mono-number text-slate-900">{formatCurrency(portfolioMetrics?.docStamps || 0)}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-slate-50 rounded">
                      <span className="text-slate-600">Intangible Stamps (0.20% of loan)</span>
                      <span className="font-mono-number text-slate-900">{formatCurrency(portfolioMetrics?.intangibleStamps || 0)}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-slate-50 rounded">
                      <span className="text-slate-600">Title Search</span>
                      <span className="font-mono-number text-slate-900">{formatCurrency(portfolioMetrics?.titleSearch || 0)}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-slate-50 rounded">
                      <span className="text-slate-600">Title Endorsements</span>
                      <span className="font-mono-number text-slate-900">{formatCurrency(portfolioMetrics?.titleEndorsements || 0)}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-slate-50 rounded">
                      <span className="text-slate-600">Lien Search</span>
                      <span className="font-mono-number text-slate-900">{formatCurrency(portfolioMetrics?.lienSearch || 0)}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-slate-50 rounded">
                      <span className="text-slate-600">Courier/Express Mail</span>
                      <span className="font-mono-number text-slate-900">{formatCurrency(portfolioMetrics?.courierExpressMail || 0)}</span>
                    </div>
                    <div className="col-span-2 flex justify-between p-3 bg-[#002F87] text-white rounded mt-2">
                      <span className="font-semibold">Total Settlement Charges</span>
                      <span className="font-mono-number font-semibold">{formatCurrency(portfolioMetrics?.totalSettlementCharges || 0)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              </>
              )}
            </div>

            {/* Summary Panel - Right Column */}
            {portfolioInputs.transactionType && (
            <div className="lg:col-span-2">
              <div className="sticky top-6">
                <Card className="border border-slate-200 shadow-lg overflow-hidden">
                  {/* Yellow accent line */}
                  <div className="h-1 bg-[#FFFF00]" />
                  
                  {/* Summary Header */}
                  <div className="bg-[#002F87] text-white px-6 py-4">
                    <h2 className="font-heading font-bold text-sm uppercase tracking-wide flex items-center gap-2">
                      <CalcIcon className="w-4 h-4" strokeWidth={1.5} />
                      Standard Loan Summary
                    </h2>
                    <p className="text-xs text-white/70 mt-1">
                      {portfolioInputs.transactionType === PORTFOLIO_TRANSACTION_TYPES.DEVELOPER && 'Developer'}
                      {portfolioInputs.transactionType === PORTFOLIO_TRANSACTION_TYPES.REFINANCE_CASHOUT && 'Refinance Cash-Out'}
                      {portfolioInputs.transactionType === PORTFOLIO_TRANSACTION_TYPES.PURCHASE && 'Purchase'}
                      {!portfolioInputs.transactionType && 'Select Transaction Type'}
                      {' | '}{portfolioInputs.amortizationTerm}yr Amortization
                    </p>
                  </div>

                  <CardContent className="p-6 space-y-6">
                    {/* Key Metrics */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2 p-4 bg-[#002F87] rounded-md">
                        <p className="text-xs text-white/80 uppercase tracking-wide mb-1">Loan Amount ({portfolioMetrics?.targetLTV || 0}% LTV)</p>
                        <p className="font-mono-number text-2xl font-bold text-white animate-number" data-testid="result-portfolio-loan">
                          {formatCurrency(portfolioMetrics?.loanAmount || 0)}
                        </p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
                        <p className="text-xs text-blue-700 uppercase tracking-wide mb-1">IO Payment</p>
                        <p className="font-mono-number text-lg font-semibold text-blue-700 animate-number">
                          {formatCurrency(portfolioMetrics?.ioPayment || 0)}
                        </p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-md border border-green-200">
                        <p className="text-xs text-green-700 uppercase tracking-wide mb-1">P&I Payment</p>
                        <p className="font-mono-number text-lg font-semibold text-green-700 animate-number">
                          {formatCurrency(portfolioMetrics?.piPayment || 0)}
                        </p>
                      </div>
                      <div className={`p-3 rounded-md border ${(portfolioMetrics?.cashToClose || 0) < 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                        <p className={`text-xs uppercase tracking-wide mb-1 ${(portfolioMetrics?.cashToClose || 0) < 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                          {(portfolioMetrics?.cashToClose || 0) < 0 ? 'Credit to Borrower' : 'Cash to Close'}
                        </p>
                        <p className={`font-mono-number text-lg font-semibold animate-number ${(portfolioMetrics?.cashToClose || 0) < 0 ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="result-portfolio-cash">
                          {(portfolioMetrics?.cashToClose || 0) < 0 
                            ? `(${formatCurrency(Math.abs(portfolioMetrics?.cashToClose || 0))})`
                            : formatCurrency(portfolioMetrics?.cashToClose || 0)}
                        </p>
                      </div>
                      {portfolioInputs.transactionType !== PORTFOLIO_TRANSACTION_TYPES.REFINANCE_CASHOUT && (
                        <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
                          <p className="text-xs text-[#637888] uppercase tracking-wide mb-1">Down Payment</p>
                          <p className="font-mono-number text-lg font-semibold text-[#002F87] animate-number">
                            {formatCurrency(portfolioMetrics?.downPayment || 0)}
                          </p>
                        </div>
                      )}
                    </div>

                    <Separator />

                    {/* Monthly Payment Breakdown */}
                    <div>
                      <h3 className="font-heading font-semibold text-sm text-[#002F87] uppercase tracking-wide mb-3">
                        Total Monthly (PITI + HOA)
                      </h3>
                      <div className="p-4 bg-[#EAF5FD] rounded-md border border-[#002F87]/20">
                        <p className="font-mono-number text-2xl font-bold text-[#002F87]" data-testid="result-portfolio-piti">
                          {formatCurrency(portfolioMetrics?.totalMonthlyPayment || 0)}
                        </p>
                      </div>
                      <div className="mt-3 space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600">P&I</span>
                          <span className="font-mono-number text-slate-900">
                            {formatCurrency(portfolioMetrics?.piPayment || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Monthly Taxes</span>
                          <span className="font-mono-number text-slate-900">
                            {formatCurrency(portfolioMetrics?.monthlyTaxes || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Monthly Insurance</span>
                          <span className="font-mono-number text-slate-900">
                            {formatCurrency(portfolioMetrics?.monthlyInsurance || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">HOA</span>
                          <span className="font-mono-number text-slate-900">
                            {formatCurrency(portfolioMetrics?.monthlyHOA || 0)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Closing Costs Breakdown */}
                    <div>
                      <h3 className="font-heading font-semibold text-sm text-[#002F87] uppercase tracking-wide mb-3">
                        Closing Costs Breakdown
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Lender Fees</span>
                          <span className="font-mono-number text-slate-900">
                            {formatCurrency(portfolioMetrics?.totalLenderFees || 0)}
                          </span>
                        </div>
                        {portfolioInputs.transactionType === PORTFOLIO_TRANSACTION_TYPES.DEVELOPER && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Developer Costs</span>
                          <span className="font-mono-number text-slate-900">
                            {formatCurrency(portfolioMetrics?.totalDeveloperCosts || 0)}
                          </span>
                        </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-slate-600">Settlement Charges</span>
                          <span className="font-mono-number text-slate-900">
                            {formatCurrency(portfolioMetrics?.totalSettlementCharges || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Pre-Paids / Escrows</span>
                          <span className="font-mono-number text-slate-900">
                            {formatCurrency(portfolioMetrics?.totalPrePaids || 0)}
                          </span>
                        </div>
                        <Separator className="my-2" />
                        <div className="flex justify-between font-semibold text-[#002F87]">
                          <span>Total Closing Costs</span>
                          <span className="font-mono-number">
                            {formatCurrency(portfolioMetrics?.totalClosingCosts || 0)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Sources & Uses */}
                    <div>
                      <h3 className="font-heading font-semibold text-sm text-[#002F87] uppercase tracking-wide mb-3">
                        Sources & Uses
                      </h3>
                      
                      <div className="mb-4">
                        <p className="text-xs text-[#637888] uppercase tracking-wide mb-2">Sources</p>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-600">Loan Amount</span>
                            <span className="font-mono-number text-slate-900">
                              {formatCurrency(portfolioMetrics?.loanAmount || 0)}
                            </span>
                          </div>
                          {(portfolioMetrics?.cashToClose || 0) >= 0 ? (
                            <div className="flex justify-between">
                              <span className="text-slate-600">Cash to Close</span>
                              <span className="font-mono-number text-slate-900">
                                {formatCurrency(portfolioMetrics?.cashToClose || 0)}
                              </span>
                            </div>
                          ) : (
                            <div className="flex justify-between text-emerald-700">
                              <span>Credit to Borrower</span>
                              <span className="font-mono-number">
                                ({formatCurrency(Math.abs(portfolioMetrics?.cashToClose || 0))})
                              </span>
                            </div>
                          )}
                          {portfolioInputs.transactionType !== PORTFOLIO_TRANSACTION_TYPES.REFINANCE_CASHOUT && (portfolioMetrics?.depositAmount > 0) && (
                            <div className="flex justify-between">
                              <span className="text-slate-600">Deposit Applied</span>
                              <span className="font-mono-number text-slate-900">
                                {formatCurrency(portfolioMetrics?.depositAmount || 0)}
                              </span>
                            </div>
                          )}
                          {portfolioInputs.transactionType !== PORTFOLIO_TRANSACTION_TYPES.REFINANCE_CASHOUT && (portfolioMetrics?.totalCredits > 0) && (
                            <div className="flex justify-between">
                              <span className="text-slate-600">Credits Applied</span>
                              <span className="font-mono-number text-slate-900">
                                {formatCurrency(portfolioMetrics?.totalCredits || 0)}
                              </span>
                            </div>
                          )}
                          <Separator className="my-2" />
                          <div className="flex justify-between font-semibold text-[#002F87]">
                            <span>Total Sources</span>
                            <span className="font-mono-number">
                              {formatCurrency(portfolioMetrics?.totalSources || 0)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs text-[#637888] uppercase tracking-wide mb-2">Uses</p>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-600">
                              {portfolioInputs.transactionType === PORTFOLIO_TRANSACTION_TYPES.REFINANCE_CASHOUT 
                                ? 'Property Value' 
                                : 'Purchase Price'}
                            </span>
                            <span className="font-mono-number text-slate-900">
                              {formatCurrency(portfolioMetrics?.purchasePrice || 0)}
                            </span>
                          </div>
                          {portfolioInputs.transactionType === PORTFOLIO_TRANSACTION_TYPES.REFINANCE_CASHOUT && (
                            <div className="flex justify-between">
                              <span className="text-slate-600">Existing Mortgage Balance</span>
                              <span className="font-mono-number text-slate-900">
                                {formatCurrency(portfolioMetrics?.existingMortgageBalance || 0)}
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-slate-600">Closing Costs</span>
                            <span className="font-mono-number text-slate-900">
                              {formatCurrency(portfolioMetrics?.totalClosingCosts || 0)}
                            </span>
                          </div>
                          {portfolioInputs.transactionType === PORTFOLIO_TRANSACTION_TYPES.REFINANCE_CASHOUT && (
                            <>
                              <Separator className="my-2" />
                              <div className="flex justify-between font-semibold text-emerald-700">
                                <span>Cash Out Amount</span>
                                <span className="font-mono-number">
                                  {formatCurrency(portfolioMetrics?.cashOutAmount || 0)}
                                </span>
                              </div>
                            </>
                          )}
                          <Separator className="my-2" />
                          <div className="flex justify-between font-semibold text-[#002F87]">
                            <span>Total Uses</span>
                            <span className="font-mono-number">
                              {formatCurrency(portfolioMetrics?.totalUses || 0)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Cash to Close Breakdown - Not for Refinance Cash-Out */}
                    {portfolioInputs.transactionType !== PORTFOLIO_TRANSACTION_TYPES.REFINANCE_CASHOUT && (portfolioMetrics?.depositAmount > 0 || portfolioMetrics?.totalCredits > 0) && (
                      <>
                        <Separator />
                        <div>
                          <h3 className="font-heading font-semibold text-sm text-[#002F87] uppercase tracking-wide mb-3">
                            Cash to Close Calculation
                          </h3>
                          <div className="space-y-2 text-sm bg-amber-50 p-3 rounded-md border border-amber-200">
                            {portfolioInputs.transactionType !== PORTFOLIO_TRANSACTION_TYPES.REFINANCE_CASHOUT && (
                              <div className="flex justify-between">
                                <span className="text-slate-600">Down Payment</span>
                                <span className="font-mono-number text-slate-900">
                                  {formatCurrency(portfolioMetrics?.downPayment || 0)}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-slate-600">+ Total Closing Costs</span>
                              <span className="font-mono-number text-slate-900">
                                {formatCurrency(portfolioMetrics?.totalClosingCosts || 0)}
                              </span>
                            </div>
                            {portfolioMetrics?.depositAmount > 0 && (
                              <div className="flex justify-between text-emerald-700">
                                <span>− Deposit ({portfolioMetrics?.depositPercent}%)</span>
                                <span className="font-mono-number">
                                  ({formatCurrency(portfolioMetrics?.depositAmount || 0)})
                                </span>
                              </div>
                            )}
                            {portfolioMetrics?.developerCredit > 0 && portfolioInputs.transactionType === PORTFOLIO_TRANSACTION_TYPES.DEVELOPER && (
                              <div className="flex justify-between text-emerald-700">
                                <span>− Developer Credit</span>
                                <span className="font-mono-number">
                                  ({formatCurrency(portfolioMetrics?.developerCredit || 0)})
                                </span>
                              </div>
                            )}
                            {portfolioMetrics?.lenderCredit > 0 && (
                              <div className="flex justify-between text-emerald-700">
                                <span>− Lender Credit</span>
                                <span className="font-mono-number">
                                  ({formatCurrency(portfolioMetrics?.lenderCredit || 0)})
                                </span>
                              </div>
                            )}
                            <Separator className="my-2 bg-amber-300" />
                            <div className={`flex justify-between font-semibold ${(portfolioMetrics?.cashToClose || 0) < 0 ? 'text-emerald-700' : 'text-amber-800'}`}>
                              <span>= {(portfolioMetrics?.cashToClose || 0) < 0 ? 'Credit to Borrower' : 'Cash to Close'}</span>
                              <span className="font-mono-number">
                                {(portfolioMetrics?.cashToClose || 0) < 0 
                                  ? `(${formatCurrency(Math.abs(portfolioMetrics?.cashToClose || 0))})`
                                  : formatCurrency(portfolioMetrics?.cashToClose || 0)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* PDF Download Button */}
                    <PDFDownloadLink
                      document={<PortfolioLoanSummaryPDF data={portfolioMetrics} inputs={portfolioInputs} loanOfficer={loanOfficer} />}
                      fileName={`portfolio-loan-summary-${portfolioInputs.projectName?.replace(/\s+/g, '-') || 'project'}-${new Date().toISOString().split('T')[0]}.pdf`}
                      className="block"
                    >
                      {({ loading }) => (
                        <Button
                          data-testid="btn-download-portfolio-pdf"
                          className="w-full h-12 bg-[#FFFF00] hover:bg-[#E6E600] text-black font-heading font-semibold uppercase tracking-wide"
                          disabled={loading || !hasPortfolioResults}
                        >
                          {loading ? (
                            <>Generating PDF...</>
                          ) : (
                            <>
                              <Download className="w-4 h-4 mr-2" strokeWidth={2} />
                              Download PDF Summary
                            </>
                          )}
                        </Button>
                      )}
                    </PDFDownloadLink>
                  </CardContent>
                </Card>
              </div>
            </div>
            )}
          </div>
        )}

        {/* Churchill DSCR Calculator */}
        {isChurchillLoan && (
          <ChurchillDSCRCalculator 
            loanOfficer={loanOfficer}
            onSave={handleSaveScenario}
          />
        )}

        {/* Indication of Interest Generator */}
        {isIOI && (
          <IOIGenerator loanOfficer={loanOfficer} controlsRef={ioiControlsRef} />
        )}
      </main>

      {/* Saved Scenarios Modal */}
      <SavedScenarios
        isOpen={savedScenariosOpen}
        onClose={() => setSavedScenariosOpen(false)}
        officerId={loanOfficer?.id}
        onLoadScenario={handleLoadScenario}
        onCompareScenarios={handleCompareScenarios}
      />

      {/* Compare View Modal */}
      {compareViewOpen && (
        <CompareView
          scenarios={compareScenarios}
          onClose={() => {
            setCompareViewOpen(false);
            setCompareScenarios([]);
          }}
        />
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-12 py-6">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-xs text-[#637888]">
            This calculator provides estimates only and does not constitute a loan commitment. 
            All figures are subject to final underwriting and approval.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Calculator;
